import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";

import { T } from "../../constants";
import { useUi } from "../../ui";
import Field from "../../vip/components/Field";

const useStyles = makeStyles({
  row: {
    padding: 20,
    borderTop: `1px solid ${T.borderSoft}`,
    "&:first-child": { borderTop: "none" },
  },
  fields: {
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr 1fr 1fr",
    gap: 12,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  description: { marginTop: 12 },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 12 },
  templateLink: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: T.surface,
    color: T.fg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": { borderColor: T.primary, color: T.primary },
  },
  secondaryButton: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    color: T.mutedFg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": { background: T.surface },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  deleteButton: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    color: T.mutedFg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": { borderColor: T.danger, color: T.danger },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
});

function toEditableContact(contact) {
  return {
    id: contact.id,
    name: contact.name || "",
    description: contact.description || "",
    phone: contact.phone || "",
    email: contact.email || "",
    smsTemplate: contact.smsTemplate ?? null,
  };
}

export default function ContactRow({ contact, designUrl, disabled, onDelete, onSave }) {
  const s = useStyles();
  const ui = useUi();
  const original = useMemo(() => toEditableContact(contact), [contact]);
  const [draft, setDraft] = useState(original);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setDraft(original);
  }, [original]);

  const setField = (field) => (event) => {
    const { value } = event.target;
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(original);
  const isBlocked = disabled || submitting;
  const canSave = Boolean(draft.name.trim()) && isDirty && !isBlocked;

  const save = async (event) => {
    event.preventDefault();
    if (!canSave) return;

    setSubmitting(true);
    try {
      await onSave(draft);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (isBlocked) return;

    setSubmitting(true);
    try {
      await onDelete(contact);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li className={s.row}>
      <form onSubmit={save}>
        <div className={s.fields}>
          <Field label="Name">
            <input className={ui.field} disabled={isBlocked} value={draft.name} onChange={setField("name")} />
          </Field>
          <Field label="SMS number">
            <input className={ui.field} disabled={isBlocked} value={draft.phone} onChange={setField("phone")} />
          </Field>
          <Field label="Email">
            <input className={ui.field} disabled={isBlocked} value={draft.email} onChange={setField("email")} />
          </Field>
          <Field label="Contact type">
            <div className={ui.field}>
              {contact.type === "SANCTIONED_PLATE" ? "Sanctioned plate" : "Booking"}
            </div>
          </Field>
        </div>

        <Field label="Description" className={s.description}>
          <input className={ui.field} disabled={isBlocked} value={draft.description} onChange={setField("description")} />
        </Field>

        <div className={s.actions}>
          <button type="button" className={s.deleteButton} disabled={isBlocked} onClick={remove}>
            Delete
          </button>
          <Link className={s.templateLink} to={designUrl}>
            Design
          </Link>
          <button type="button" className={s.secondaryButton} disabled={!isDirty || isBlocked} onClick={() => setDraft(original)}>
            Cancel
          </button>
          <button type="submit" className={ui.primaryBtn} disabled={!canSave}>
            Save
          </button>
        </div>
      </form>
    </li>
  );
}
